/**
 * Rehab page. Mirrors design/Syntra/rehab.html section-for-section: page
 * header → KPI row → filter tabs → card grid → right-side detail drawer.
 *
 * The drawer (`RehabDetailDrawer`) replaces the older Dialog/Tabs detail
 * surface entirely. Documents and CompanyCam integrations still exist in
 * `useRehab` for any future surface that needs them; they're intentionally
 * not surfaced here so the page matches the design exactly.
 */
import { useEffect, useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Hammer } from "lucide-react";

import { RehabDetailDrawer } from "@/components/rehab/RehabDetailDrawer";
import {
  RehabFilterTabs,
  type RehabFilter,
} from "@/components/rehab/RehabFilterTabs";
import { RehabKpiRow } from "@/components/rehab/RehabKpiRow";
import { RehabProjectCard } from "@/components/rehab/RehabProjectCard";
import { deriveStatus } from "@/components/rehab/rehab-status";
import { RentalListingBlockedDialog } from "@/components/rentals/RentalListingBlockedDialog";
import { useRehab, type RehabProject } from "@/hooks/use-rehab";

export default function Rehab() {
  const {
    projects,
    isLoading,
    refetch,
    rentalListingBlocked,
    setRentalListingBlocked,
    updateProject,
    startProject,
    completeProject,
  } = useRehab();

  const [selectedProject, setSelectedProject] = useState<RehabProject | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [filter, setFilter] = useState<RehabFilter>("all");

  // Keep the open drawer's project in sync with the latest server data
  // (start/complete mutations refetch the list — we want the new dates/stage
  // reflected in the drawer without reopening it).
  const selectedProjectId = selectedProject?.id;
  useEffect(() => {
    if (!selectedProjectId || !detailOpen) return;
    const updated = projects.find((p) => p.id === selectedProjectId);
    if (updated) setSelectedProject(updated);
  }, [projects, selectedProjectId, detailOpen]);

  const counts: Record<RehabFilter, number> = {
    all: projects.length,
    not_started: 0,
    in_progress: 0,
    completed: 0,
  };
  for (const p of projects) counts[deriveStatus(p)] += 1;

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((p) => deriveStatus(p) === filter);

  const handleOpen = (project: RehabProject) => {
    setSelectedProject(project);
    setDetailOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Rehab
        </h1>
        <p className="text-sm text-muted-foreground">
          Track scope, progress, budget and days for every property in rehab.
        </p>
      </div>

      {projects.length > 0 && <RehabKpiRow projects={projects} />}

      {projects.length > 0 && (
        <RehabFilterTabs current={filter} onChange={setFilter} counts={counts} />
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-72 w-full rounded-lg" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="py-16 text-center">
            <Hammer className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
            <p className="font-semibold text-foreground">No properties to show</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Move a deal from{" "}
              <span className="font-semibold text-primary">Pipeline</span>'s
              Closed Won column to start tracking rehab progress here.
            </p>
          </CardContent>
        </Card>
      ) : filteredProjects.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="py-16 text-center">
            <Hammer className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
            <p className="font-semibold text-foreground">
              No properties match this filter
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Try switching to a different status.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <RehabProjectCard
              key={project.id}
              project={project}
              onOpen={handleOpen}
              onStart={startProject}
              onComplete={completeProject}
            />
          ))}
        </div>
      )}

      <RehabDetailDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        project={selectedProject}
        updateProject={updateProject}
        startProject={startProject}
        completeProject={completeProject}
        refresh={refetch}
      />

      {/* Blocks "Start Rehab" while the property is actively listed for rent. */}
      <RentalListingBlockedDialog
        open={rentalListingBlocked}
        onOpenChange={setRentalListingBlocked}
      />
    </div>
  );
}
